/**
 * Schedule hooks (task 1003).
 *
 * Report: `project/reports/innovate-service-endpoint-schedules.md`.
 *
 * Four reads and three writes over task 1000's `/schedules` routes, in the
 * app's standard React Query idiom:
 *
 *   1. **One service's schedules** — what the Schedules section lists.
 *   2. **The account's active-schedule count** — the numerator of the quota the
 *      section header shows. It is an ACCOUNT total, not this service's, because
 *      `max_schedules` is an account cap: the service counts every active row
 *      the Account holds when it decides whether the next create fits.
 *   3. **One schedule's run log** — lazy, fetched when a row's occurrences are
 *      opened (the `EndpointRequestHistory` expanded-row precedent).
 *   4. **Schedule attribution for a service's runs** — which `EndpointRequest`
 *      each schedule produced, so the Testing tab's run history can label a run
 *      "Scheduled" and name the schedule.
 *
 * Why (4) is assembled here rather than read from a field: there is no
 * `scheduleId` on `EndpointRequest` (kv decision 2026-09-24) and the request
 * list does not resolve one, so the run log is the only join that exists. The
 * schedules of one AI Service are few, and their newest log pages are small, so
 * the map is built from those pages and nothing is invented when a run predates
 * them.
 */
import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { collaborationsApi, schedulesApi } from '@/services/api';
import type {
  CreateScheduleInput,
  Schedule,
  ScheduleRun,
  UpdateScheduleInput,
} from '@/types/schedules';

/** Every schedules read hangs off this prefix, so one write can clear them all. */
export const SCHEDULES_QUERY_KEY = ['schedules'] as const;

export const schedulesForEndpointQueryKey = (endpointId: string | undefined) =>
  [...SCHEDULES_QUERY_KEY, 'endpoint', endpointId] as const;

export const scheduleRunsQueryKey = (scheduleId: string | undefined) =>
  [...SCHEDULES_QUERY_KEY, 'runs', scheduleId] as const;

export const activeScheduleCountQueryKey = [...SCHEDULES_QUERY_KEY, 'active-count'] as const;

/** How many of a schedule's newest occurrences a row's list shows. */
const RUN_LOG_PAGE_SIZE = 10;

/** The schedules on one AI Service, newest first. */
export const useSchedules = (
  endpointId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const query = useQuery({
    queryKey: schedulesForEndpointQueryKey(endpointId),
    queryFn: () =>
      schedulesApi.list({ endpointId, limit: 100 }).then((res) => res.data.data ?? []),
    enabled: !!endpointId && options?.enabled !== false,
  });

  return { ...query, schedules: query.data ?? [] };
};

/**
 * How many ACTIVE schedules the Account holds — the number the service compares
 * to `max_schedules`. Read as a one-row page for its `pagination.total` rather
 * than by counting a list the app would otherwise never use.
 */
export const useActiveScheduleCount = (options?: { enabled?: boolean }) => {
  const query = useQuery({
    queryKey: activeScheduleCountQueryKey,
    queryFn: () =>
      schedulesApi
        .list({ status: 'active', limit: 1 })
        .then((res) => res.data.pagination?.total ?? 0),
    enabled: options?.enabled !== false,
  });

  return { ...query, activeCount: query.data ?? null };
};

/** One schedule's occurrences, newest first. Lazy — the row asks for them. */
export const useScheduleRuns = (scheduleId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: scheduleRunsQueryKey(scheduleId),
    queryFn: () =>
      schedulesApi
        .listRuns(scheduleId!, 1, RUN_LOG_PAGE_SIZE)
        .then((res) => res.data.data ?? []),
    enabled: enabled && !!scheduleId,
  });

/**
 * The newest occurrences of several schedules at once, keyed by schedule id.
 *
 * ONE query per schedule, under the same key `useScheduleRuns` uses, so the
 * Schedules list's Last-run column and a row's expanded occurrences read the
 * same cached page instead of asking twice. A service holds a handful of
 * schedules — the plan caps the Account at tens — so this stays a handful of
 * small reads.
 */
export const useScheduleRunLogs = (
  schedules: Schedule[],
  options?: { enabled?: boolean }
): Map<string, ScheduleRun[]> => {
  const enabled = options?.enabled !== false;

  const runQueries = useQueries({
    queries: schedules.map((schedule) => ({
      queryKey: scheduleRunsQueryKey(schedule.id),
      queryFn: () =>
        schedulesApi.listRuns(schedule.id, 1, RUN_LOG_PAGE_SIZE).then((res) => res.data.data ?? []),
      enabled,
    })),
  });

  // The queries array is rebuilt every render, so the memo is keyed on what
  // actually changes: which schedules there are and which log rows came back.
  const runRows = runQueries.map((result) => (result.data as ScheduleRun[] | undefined) ?? []);
  const scheduleSignature = schedules.map((schedule) => schedule.id).join('|');
  const runSignature = runRows
    .map((rows) => rows.map((row) => `${row.id}:${row.state}:${row.endpointRequestId ?? ''}`).join(','))
    .join('|');

  return useMemo(() => {
    const byScheduleId = new Map<string, ScheduleRun[]>();
    schedules.forEach((schedule, index) => {
      byScheduleId.set(schedule.id, runRows[index] ?? []);
    });
    return byScheduleId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSignature, runSignature]);
};

/**
 * `endpointRequestId` → the collaboration id the run detail page is addressed
 * by, for the occurrences that became a call.
 *
 * A run-log row names the REQUEST, and `/endpoints/:id/runs/:collaborationId`
 * names the COLLABORATION; the request list does not carry the collaboration
 * either, so the one lookup that exists is the collaborations list filtered by
 * `requestId`. Only the occurrences currently on screen are resolved, and a
 * request whose collaboration cannot be found renders without a link rather
 * than with a dead one.
 */
export const useRunLinksForRequests = (requestIds: string[]): Map<string, string> => {
  const unique = [...new Set(requestIds.filter(Boolean))];

  const queries = useQueries({
    queries: unique.map((requestId) => ({
      queryKey: ['collaborations', 'by-request', requestId] as const,
      queryFn: () =>
        collaborationsApi
          .getAll(1, 1, { requestId })
          .then((res) => res.data.data?.[0]?.id ?? null),
      staleTime: 60_000,
    })),
  });

  const signature = unique
    .map((requestId, index) => `${requestId}:${(queries[index]?.data as string | null) ?? ''}`)
    .join('|');

  return useMemo(() => {
    const links = new Map<string, string>();
    unique.forEach((requestId, index) => {
      const collaborationId = queries[index]?.data as string | null | undefined;
      if (collaborationId) links.set(requestId, collaborationId);
    });
    return links;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
};

/** Which schedule produced a given run, for the run history's label. */
export interface ScheduleAttribution {
  scheduleId: string;
  scheduleName: string;
}

/**
 * `endpointRequestId` → the schedule that produced it, for one AI Service.
 *
 * Built from the service's schedules and the newest page of each one's run log,
 * because that log is the ONLY join there is: nothing on `EndpointRequest`
 * names a schedule, and the request list does not resolve one. A run older than
 * the pages read here simply carries no label, which is the honest answer —
 * never a guessed one.
 */
export const useScheduleAttribution = (
  endpointId: string | undefined,
  options?: { enabled?: boolean }
): Map<string, ScheduleAttribution> => {
  const active = options?.enabled !== false && !!endpointId;
  const { schedules } = useSchedules(endpointId, { enabled: active });
  const runLogs = useScheduleRunLogs(schedules, { enabled: active });

  const signature = schedules
    .map((schedule) => `${schedule.id}:${schedule.name}:${(runLogs.get(schedule.id) ?? []).length}`)
    .join('|');

  return useMemo(() => {
    const attribution = new Map<string, ScheduleAttribution>();
    schedules.forEach((schedule: Schedule) => {
      (runLogs.get(schedule.id) ?? []).forEach((row) => {
        if (row.endpointRequestId) {
          attribution.set(row.endpointRequestId, {
            scheduleId: schedule.id,
            scheduleName: schedule.name,
          });
        }
      });
    });
    return attribution;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
};

/**
 * What a write can have changed: this service's list and the account's active
 * count. Deliberately NOT the whole `schedules` prefix — that would also
 * invalidate the run log of a schedule the write just deleted, and refetching
 * it answers 404 for a row that is gone on purpose.
 */
const invalidateSchedules = (
  queryClient: ReturnType<typeof useQueryClient>,
  endpointId?: string
) => {
  void queryClient.invalidateQueries({ queryKey: activeScheduleCountQueryKey });
  if (endpointId) {
    void queryClient.invalidateQueries({ queryKey: schedulesForEndpointQueryKey(endpointId) });
  }
};

export const useCreateSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateScheduleInput) =>
      schedulesApi.create(data).then((res) => res.data.data),
    onSuccess: (created) => invalidateSchedules(queryClient, created.endpointId),
  });
};

/** Edit, pause and resume are one write — they differ only in the fields sent. */
export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduleInput }) =>
      schedulesApi.update(id, data).then((res) => res.data.data),
    onSuccess: (updated) => invalidateSchedules(queryClient, updated.endpointId),
  });
};

export const useDeleteSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // DELETE returns the deleted row, so the endpointId rides back with it.
    mutationFn: (id: string) => schedulesApi.delete(id).then((res) => res.data.data),
    onSuccess: (deleted) => invalidateSchedules(queryClient, deleted.endpointId),
  });
};
